import { assertUnreachable, isPresent } from '@luminovo/commons';
import {
    FullPart,
    isCustomComponentFull,
    isCustomFullPart,
    isGenericFullPart,
    isOtsComponentFull,
    isOtsFullPart,
    OtsFullPart,
    PartLite,
    PartLiteTypes,
    StandardPartDTO,
    StandardPartTypes,
} from '@luminovo/http-client';
import { FormFieldsConfiguration, OfferLineItem, PdfOfferLineItem } from '../../types';

export function resolveOtsPartsRecursively(
    part: PartLite | undefined | null,
): Array<PartLite & { kind: PartLiteTypes.OffTheShelf }> {
    if (!isPresent(part)) {
        return [];
    }
    if (part.kind === PartLiteTypes.OffTheShelf) {
        return [part];
    }
    if (part.kind === PartLiteTypes.Generic) {
        return part.matches.flatMap((m) => resolveOtsPartsRecursively(m));
    }
    if (part.kind === PartLiteTypes.Ipn) {
        return part.matches.flatMap((m) => resolveOtsPartsRecursively(m));
    }
    if (part.kind === PartLiteTypes.RawSpecification) {
        return [];
    }
    if (part.kind === PartLiteTypes.Custom) {
        return [];
    }
    if (part.kind === PartLiteTypes.CustomComponent) {
        return [];
    }
    if (part.kind === PartLiteTypes.Unknown) {
        return [];
    }
    assertUnreachable(part);
}

export function resolveOtsFullPartsRecursively(part: FullPart | undefined | null): Array<OtsFullPart> {
    if (!isPresent(part)) {
        return [];
    }
    if (isOtsFullPart(part)) {
        return [part];
    }
    if (isGenericFullPart(part)) {
        return part.matches.flatMap((m) => resolveOtsFullPartsRecursively(m));
    }
    if (isOtsComponentFull(part)) {
        return part.matches.flatMap((m) => resolveOtsFullPartsRecursively(m.part.data));
    }
    if (isCustomComponentFull(part)) {
        return [];
    }
    if (isCustomFullPart(part)) {
        return [];
    }
    assertUnreachable(part);
}

export function columnId(col: keyof FormFieldsConfiguration): string {
    return col;
}

export function convertPartLiteToStandardPart(partLite: PartLite): StandardPartDTO {
    switch (partLite.kind) {
        case PartLiteTypes.OffTheShelf:
            return { type: StandardPartTypes.OffTheShelf, data: partLite.id };
        case PartLiteTypes.Generic:
            return { type: StandardPartTypes.Generic, data: partLite.id };
        case PartLiteTypes.Ipn:
            return { type: StandardPartTypes.Ipn, data: partLite.id };
        case PartLiteTypes.RawSpecification:
        case PartLiteTypes.Custom:
        case PartLiteTypes.CustomComponent:
        case PartLiteTypes.Unknown:
            throw new Error('Cannot convert part lite to standard part');
    }
}

export function convertOtsFullPartToPartLite(part: OtsFullPart): PartLite & { kind: PartLiteTypes.OffTheShelf } {
    return {
        kind: PartLiteTypes.OffTheShelf,
        id: part.id,
        manufacturer: part.manufacturer,
        mpn: part.mpn,
        part_category: part.part_category?.path ?? [],
    };
}

export function isCustomPartRow(row: OfferLineItem): boolean {
    return row.part.kind === PartLiteTypes.Custom || row.part.kind === PartLiteTypes.CustomComponent;
}

function isCustomPartFieldEditable(row?: PdfOfferLineItem): boolean {
    const partKind = row?.part.value?.kind;
    switch (partKind) {
        case PartLiteTypes.OffTheShelf:
        case PartLiteTypes.Generic:
        case PartLiteTypes.Ipn:
        case PartLiteTypes.RawSpecification:
            return true;
        case undefined:
        case PartLiteTypes.Custom:
        case PartLiteTypes.CustomComponent:
        case PartLiteTypes.Unknown:
            return false;
    }
}

export function isStockEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isPackagingEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isSupplierPartNumberEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isReachEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isRohsEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isEccnNumbersEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isHtsCodeEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isCountryOfOriginEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isNcnrEditable(row?: PdfOfferLineItem): boolean {
    return isCustomPartFieldEditable(row);
}

export function isBidEditable(row?: PdfOfferLineItem): boolean {
    // Bid is typically editable for all part types
    return true;
}
